import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';

// Map backend/assigned status to a timeline position
const STATUS_ORDER = [
  'Scheduled',
  'DriverStarted',
  'ReachedFactory',
  'Loading',
  'InTransit',
  'ReachedCustomer',
  'Delivered'
];

function buildTimeline(currentStatus) {
  const idx = Math.max(STATUS_ORDER.indexOf(currentStatus), 0);
  const steps = [
    'Order Scheduled (Confirmed)',
    'Driver Started to Plant (Pickup Concrete)',
    'Driver Reached Factory (Plant)',
    'Loading the Concrete',
    'Started Trip to Customer',
    'Reached Customer Location (Unloading)',
    'Order Completed'
  ];
  return steps.map((step, i) => ({
    step,
    completed: i < idx,
    current: i === idx
  }));
}

function normalizeStatus(status) {
  if (!status) return 'Scheduled';
  const s = String(status).toLowerCase();
  if (s.includes('deliver')) return 'Delivered';
  if (s.includes('schedule')) return 'Scheduled';
  if (s.includes('start')) return 'DriverStarted';
  if (s.includes('reach') && s.includes('factory')) return 'ReachedFactory';
  if (s.includes('load')) return 'Loading';
  if (s.includes('transit') || s.includes('trip')) return 'InTransit';
  if (s.includes('reach') || s.includes('unload')) return 'ReachedCustomer';
  return 'Scheduled';
}

export default function OrderDetails() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const data = useMemo(() => {
    if (!state) return null;
    return {
      orderId: state.orderId,
      orderNumber: state.orderNumber,
      factoryName: state.factoryName,
      customerName: state.customerName,
      distanceKm: state.distanceKm,
      pickupLocation: state.pickupLocation,
      deliveryLocation: state.deliveryLocation,
      truck: state.truck || {},
      status: normalizeStatus(state.status)
    };
  }, [state]);

  if (!data) {
    return (
      <div style={{ padding: 20 }}>
        <Card className="cyber-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <strong style={{ color: '#fff' }}>No order data provided</strong>
            <Button label="Go Back" icon="pi pi-arrow-left" onClick={() => navigate(-1)} />
          </div>
          <div style={{ marginTop: 8, color: 'var(--cy-muted)' }}>
            Open this page by clicking a truck assignment in Schedule.
          </div>
        </Card>
      </div>
    );
  }

  const timeline = buildTimeline(data.status);

  return (
    <div style={{ padding: 20, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ margin: 0, color: '#fff' }}>Order Details</h2>
          <div style={{ color: 'var(--cy-muted)' }}>{data.orderNumber}</div>
        </div>
        <Button label="Back" icon="pi pi-arrow-left" onClick={() => navigate(-1)} />
      </div>

      {/* Top summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12 }}>
        <Card className="cyber-card">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <div>
              <div style={{ color: 'var(--cy-muted)', fontSize: 12 }}>Factory</div>
              <div style={{ color: '#fff', fontWeight: 600 }}>{data.factoryName || '-'}</div>
            </div>
            <div>
              <div style={{ color: 'var(--cy-muted)', fontSize: 12 }}>Customer</div>
              <div style={{ color: '#fff', fontWeight: 600 }}>{data.customerName || '-'}</div>
            </div>
            <div>
              <div style={{ color: 'var(--cy-muted)', fontSize: 12 }}>Distance</div>
              <div style={{ color: '#fff', fontWeight: 600 }}>{data.distanceKm ? `${data.distanceKm} km` : '-'}</div>
            </div>
            <div>
              <div style={{ color: 'var(--cy-muted)', fontSize: 12 }}>Truck</div>
              <div style={{ color: '#fff', fontWeight: 600 }}>{data.truck.number || '-'}</div>
            </div>
          </div>
        </Card>

        <Card className="cyber-card">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ color: 'var(--cy-muted)', fontSize: 12 }}>Driver</div>
              <div style={{ color: '#fff', fontWeight: 600 }}>{data.truck.driverName || '-'}</div>
            </div>
            <div>
              <div style={{ color: 'var(--cy-muted)', fontSize: 12 }}>Status</div>
              <span style={{
                background: data.status === 'Delivered' ? 'rgba(52,211,153,0.15)' : data.status === 'InTransit' ? 'rgba(59,130,246,0.15)' : 'rgba(234,179,8,0.15)',
                border: data.status === 'Delivered' ? '1px solid rgba(52,211,153,0.35)' : data.status === 'InTransit' ? '1px solid rgba(59,130,246,0.35)' : '1px solid rgba(234,179,8,0.35)',
                color: data.status === 'Delivered' ? '#34d399' : data.status === 'InTransit' ? '#60a5fa' : '#eab308',
                padding: '4px 10px', borderRadius: 8, fontWeight: 700
              }}>{data.status}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Progress */}
      <Card className="cyber-card">
        <div style={{ color: '#fff', fontWeight: 700, marginBottom: 8 }}>Order Progress</div>
        <div style={{ display: 'grid', gap: 10 }}>
          {timeline.map((item, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '24px 1fr', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: item.completed ? '#34d399' : item.current ? '#60a5fa' : 'rgba(255,255,255,0.2)',
                  background: item.completed ? 'rgba(52,211,153,0.15)' : item.current ? 'rgba(59,130,246,0.15)' : 'transparent'
                }} />
              </div>
              <div style={{ color: item.completed ? '#fff' : 'var(--cy-muted)' }}>{item.step}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}